import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, EyeOff, LogOut, ExternalLink, FileText, Film, Download } from "lucide-react";
import { toast } from "sonner";
import { api, imageUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState(null);

  useDocumentMeta({ title: "Manage Insights", path: "/admin" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/admin/insights");
      setArticles(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load articles");
      setArticles([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleStatus = async (article) => {
    const next = article.status === "published" ? "draft" : "published";
    try {
      await api.patch(`/api/admin/insights/${article.id}/status`, { status: next });
      toast.success(next === "published" ? "Article published" : "Moved to draft");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const remove = async (article) => {
    if (!window.confirm(`Delete "${article.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/admin/insights/${article.id}`);
      toast.success("Article deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F1F1EE]">
      {/* Top bar */}
      <header className="bg-charcoal text-canvas">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-gold" />
            <span className="font-sans text-[11px] tracking-[0.28em] uppercase text-gold">
              Insights CMS
            </span>
          </div>
          <div className="flex items-center gap-6">
            <Link
              to="/admin/enquiries"
              data-testid="nav-enquiries"
              className="font-sans text-[11px] tracking-[0.16em] uppercase text-canvas/60 hover:text-gold transition-colors"
            >
              Enquiries
            </Link>
            <Link
              to="/insights"
              target="_blank"
              className="hidden sm:inline font-sans text-[11px] tracking-[0.16em] uppercase text-canvas/60 hover:text-gold transition-colors"
            >
              View Site
            </Link>
            <span className="hidden md:inline font-sans text-[11px] tracking-wide text-canvas/50">
              {user?.email}
            </span>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 font-sans text-[11px] tracking-[0.16em] uppercase text-canvas/70 hover:text-gold transition-colors"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.6} />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-14">
        <div className="flex items-end justify-between gap-6 mb-12">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl text-charcoal leading-tight">
              Articles
            </h1>
            <p className="mt-3 font-sans text-sm text-charcoal/55">
              {articles ? `${articles.length} total` : "Loading…"}
            </p>
          </div>
          <Link
            to="/admin/new"
            data-testid="new-article-button"
            className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.14em] uppercase font-bold px-6 py-3.5 bg-forest text-canvas border border-forest hover:bg-charcoal hover:border-charcoal transition-colors duration-300"
          >
            <Plus className="w-4 h-4" strokeWidth={2} />
            New Article
          </Link>
        </div>

        {articles && articles.length === 0 && (
          <div
            data-testid="empty-state"
            className="border border-dashed border-charcoal/20 bg-canvas py-24 text-center"
          >
            <FileText className="w-10 h-10 text-charcoal/25 mx-auto" strokeWidth={1.2} />
            <p className="mt-4 font-serif text-lg text-charcoal/60">No articles yet.</p>
            <p className="mt-1 font-sans text-sm text-charcoal/40">
              Create your first Insight to get started.
            </p>
          </div>
        )}

        <div className="space-y-px bg-charcoal/10 border border-charcoal/10">
          {(articles || []).map((a) => (
            <div
              key={a.id}
              data-testid={`admin-article-row-${a.id}`}
              className="bg-canvas flex flex-col sm:flex-row sm:items-center gap-4 p-5"
            >
              <div className="w-full sm:w-28 h-20 bg-[#E4E4DE] border border-charcoal/10 shrink-0 overflow-hidden flex items-center justify-center">
                {a.cover_image_id ? (
                  <img src={imageUrl(a.cover_image_id)} alt={a.title} className="w-full h-full object-cover" />
                ) : (
                  <FileText className="w-6 h-6 text-charcoal/25" strokeWidth={1.2} />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span
                    className={`font-sans text-[10px] tracking-[0.16em] uppercase px-2.5 py-1 border ${
                      a.status === "published"
                        ? "text-forest border-forest/40 bg-forest/5"
                        : "text-charcoal/50 border-charcoal/20 bg-charcoal/[0.03]"
                    }`}
                  >
                    {a.status}
                  </span>
                  <span className="inline-flex items-center gap-1.5 font-sans text-[10px] tracking-[0.16em] uppercase text-charcoal/45">
                    {a.content_type === "external" && (<><ExternalLink className="w-3 h-3" strokeWidth={1.6} /> Link</>)}
                    {a.content_type === "internal" && (<><FileText className="w-3 h-3" strokeWidth={1.6} /> On-site</>)}
                    {a.content_type === "video" && (<><Film className="w-3 h-3" strokeWidth={1.6} /> Video</>)}
                    {a.content_type === "resource" && (<><Download className="w-3 h-3" strokeWidth={1.6} /> Resource</>)}
                  </span>
                </div>
                <h3 className="mt-2 font-serif text-lg text-charcoal leading-snug truncate">
                  {a.title}
                </h3>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  data-testid={`toggle-status-${a.id}`}
                  onClick={() => toggleStatus(a)}
                  title={a.status === "published" ? "Unpublish" : "Publish"}
                  className="inline-flex items-center justify-center w-10 h-10 border border-charcoal/15 text-charcoal/70 hover:border-forest hover:text-forest transition-colors"
                >
                  {a.status === "published" ? <EyeOff className="w-4 h-4" strokeWidth={1.6} /> : <Eye className="w-4 h-4" strokeWidth={1.6} />}
                </button>
                <Link
                  data-testid={`edit-article-${a.id}`}
                  to={`/admin/edit/${a.id}`}
                  className="inline-flex items-center justify-center w-10 h-10 border border-charcoal/15 text-charcoal/70 hover:border-forest hover:text-forest transition-colors"
                >
                  <Pencil className="w-4 h-4" strokeWidth={1.6} />
                </Link>
                <button
                  data-testid={`delete-article-${a.id}`}
                  onClick={() => remove(a)}
                  className="inline-flex items-center justify-center w-10 h-10 border border-charcoal/15 text-charcoal/70 hover:border-red-500 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.6} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
